// src/hooks/useReps.js
import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, orderBy, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './useAuth';

// Hook for admin to get all reps (includes legacy 'wholesaler' role)
export function useReps() {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading || !user || !isAdmin) {
      if (!authLoading) setLoading(false);
      return;
    }

    setLoading(true);
    // Query for both 'rep' and legacy 'wholesaler' roles
    const q = query(
      collection(db, 'users'),
      where('role', 'in', ['rep', 'wholesaler']),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const repList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setReps(repList);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching reps:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, authLoading, isAdmin]);

  const activeReps = reps.filter(r => r.active !== false);
  const inactiveReps = reps.filter(r => r.active === false);
  const pendingReps = reps.filter(r => !r.approved);
  const approvedReps = reps.filter(r => r.approved);

  return { 
    reps, 
    activeReps,
    inactiveReps,
    pendingReps,
    approvedReps,
    loading, 
    error 
  };
}

// Hook to get a single rep by ID
export function useRep(repId) {
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!repId) {
      setRep(null);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'users', repId),
      (doc) => {
        if (doc.exists()) {
          setRep({ id: doc.id, ...doc.data() });
        } else {
          setRep(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching rep:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [repId]);

  return { rep, loading, error };
}
